import { Quill } from "react-quill-new";

/** Percent of the file's own pixel width. 100 means no width attribute. */
export const IMAGE_SCALE_MIN = 10;
export const IMAGE_SCALE_MAX = 200;

const SCALE_INPUT = /^\d{1,3}$/;
const WIDTH_PX = /^\d+$/;

/** Accept only an integer percent inside the allowed range. Anything else is ignored. */
export const parseScaleInput = (raw: string): number | null => {
    const value = String(raw ?? "").trim();
    if (!SCALE_INPUT.test(value)) return null;
    const scale = Number(value);
    if (!Number.isInteger(scale) || scale < IMAGE_SCALE_MIN || scale > IMAGE_SCALE_MAX) return null;
    return scale;
};

/**
 * Percent implied by the image's width attribute and its natural pixels.
 * Null until the file has loaded, or when the stored width is not a plain pixel count.
 * Reading never writes.
 */
export const readImageScale = (img: HTMLImageElement): number | null => {
    const natural = img.naturalWidth;
    if (!Number.isFinite(natural) || natural < 1) return null;
    const raw = String(img.getAttribute("width") || "").trim();
    if (!raw) return 100;
    if (!WIDTH_PX.test(raw)) return null;
    const px = Number(raw);
    if (px < 1) return null;
    return Math.round((px / natural) * 100);
};

const imageIndex = (quill: Quill, img: HTMLImageElement): number | null => {
    if (!quill.root.contains(img)) return null;
    const blot = Quill.find(img);
    if (!blot) return null;
    try {
        const index = quill.getIndex(blot as never);
        return Number.isFinite(index) && index >= 0 ? index : null;
    } catch {
        return null;
    }
};

/**
 * Store the scale as Quill's own `width` attribute (pixels of the original).
 * 100% removes it, so existing pictures without a width stay unchanged.
 * Does not set height, so the ratio stays the file's own.
 */
export const applyImageScale = (quill: Quill, img: HTMLImageElement, percent: number): boolean => {
    const scale = parseScaleInput(String(percent));
    if (scale == null) return false;
    const natural = img.naturalWidth;
    if (!Number.isFinite(natural) || natural < 1) return false;
    const index = imageIndex(quill, img);
    if (index == null) return false;

    try {
        if (scale === 100) {
            if (!img.getAttribute("width")) return true;
            quill.formatText(index, 1, "width", false, "user");
            return !img.getAttribute("width");
        }

        const px = Math.round((natural * scale) / 100);
        if (!Number.isFinite(px) || px < 1) return false;
        quill.formatText(index, 1, "width", String(px), "user");
        return img.getAttribute("width") === String(px);
    } catch {
        return false;
    }
};
